import React from 'react';
import { Link } from 'react-router-dom';
import LegalPage from './LegalPage';

const INK  = '#0A1F4A';
const INK2 = '#3D4F78';
const RULE = '#D6DAE4';
const ACC  = '#FF7A3D';

// TODO: update when company is incorporated
const PRIVACY_EMAIL = 'privacy@jyto.eu';
const COMPANY_NAME  = 'Jyto B.V.';
const COMPANY_ADDR  = '[Registered address — to be updated upon incorporation]';

const s = {
  lead:   { fontSize: 16, lineHeight: 1.75, color: INK2, margin: '0 0 40px', paddingBottom: 40, borderBottom: `1px solid ${RULE}` },
  h2:     { fontSize: 19, fontWeight: 700, color: INK, margin: '40px 0 12px', letterSpacing: '-0.02em', paddingTop: 40, borderTop: `1px solid ${RULE}` },
  h2top:  { fontSize: 19, fontWeight: 700, color: INK, margin: '0 0 12px', letterSpacing: '-0.02em' },
  h3:     { fontSize: 15, fontWeight: 600, color: INK, margin: '24px 0 8px' },
  p:      { fontSize: 15, lineHeight: 1.75, color: INK2, margin: '0 0 16px' },
  ul:     { fontSize: 15, lineHeight: 1.75, color: INK2, paddingLeft: 24, margin: '0 0 16px' },
  li:     { marginBottom: 8 },
  table:  { width: '100%', borderCollapse: 'collapse', fontSize: 13, margin: '16px 0 24px', display: 'block', overflowX: 'auto' },
  th:     { textAlign: 'left', padding: '8px 12px', background: '#F6F7FB', borderBottom: `1px solid ${RULE}`, color: INK, fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' },
  td:     { padding: '10px 12px', borderBottom: `1px solid ${RULE}`, color: INK2, verticalAlign: 'top' },
  badge:  { display: 'inline-block', fontFamily: '"JetBrains Mono", monospace', fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', padding: '3px 8px', borderRadius: 4, background: '#E8F5E9', color: '#2E7D32', marginRight: 6 },
  note:   { background: '#F6F7FB', border: `1px solid ${RULE}`, borderRadius: 8, padding: '16px 20px', margin: '0 0 16px', fontSize: 14, lineHeight: 1.7, color: INK2 },
  a:      { color: ACC, textDecoration: 'none', fontWeight: 500 },
};

const SUB_PROCESSORS = [
  ['Supabase Inc.',         'Primary database',                           'US (EU hosting available)', 'SCCs + DPF where applicable'],
  ['Render Services, Inc.', 'Backend API hosting',                        'US',                        'SCCs'],
  ['Cloudflare, Inc.',      'Frontend hosting and CDN',                   'US',                        'SCCs + DPF'],
  ['OpenAI LLC',            'Text embeddings for profile matching',       'US',                        'SCCs + DPF'],
  ['Anthropic PBC',         'AI tender scoring and translation',          'US',                        'SCCs'],
  ['Pinecone Systems Inc.', 'Vector similarity database',                 'US',                        'SCCs'],
  ['Stripe Inc.',           'Payment processing',                         'US',                        'SCCs + DPF'],
  ['Resend Inc.',           'Transactional email',                        'US',                        'SCCs'],
];

const RIGHTS = [
  {
    title: 'Right of access (Art. 15)',
    desc: 'You can request a copy of the personal data we hold about you, along with information about how and why we process it.',
  },
  {
    title: 'Right to rectification (Art. 16)',
    desc: 'You can ask us to correct inaccurate personal data or complete incomplete data. Most profile data can be updated directly in your account settings.',
  },
  {
    title: 'Right to erasure (Art. 17)',
    desc: 'You can request deletion of your personal data. We will comply unless we have a lawful reason to retain it (e.g. financial records required by law). You can also delete your account directly from account settings.',
  },
  {
    title: 'Right to restriction (Art. 18)',
    desc: 'You can ask us to pause processing your data in certain circumstances — for example, while you contest its accuracy or while we assess an objection.',
  },
  {
    title: 'Right to data portability (Art. 20)',
    desc: 'You can request a machine-readable copy of personal data you provided to us for the purpose of performing our contract with you.',
  },
  {
    title: 'Right to object (Art. 21)',
    desc: 'You can object to processing based on our legitimate interests. We will stop unless we can demonstrate compelling legitimate grounds that override your interests.',
  },
  {
    title: 'Rights related to automated decision-making (Art. 22)',
    desc: 'Match verdicts (Great / Good / Maybe) are generated automatically, but they have no legal or similarly significant effect on you — they are suggestions only. You may, however, request a human review of any verdict result by contacting us.',
  },
];

const RETENTION = [
  ['Account and profile data',      'Duration of account + 30 days after deletion',        'Contract performance'],
  ['Match and verdict data',        'Duration of account + 30 days after deletion',        'Contract performance'],
  ['Financial and billing records', 'Up to 7 years',                                       'Legal obligation'],
  ['Anonymised usage analytics',    'Indefinitely (not personal data once anonymised)',     'Legitimate interests'],
];

export default function GDPRData() {
  return (
    <LegalPage title="GDPR & data" kicker="Data & privacy" lastUpdated="9 May 2026">

      <p style={s.lead}>
        Jyto is committed to processing personal data lawfully, transparently, and in accordance with
        the General Data Protection Regulation (GDPR — Regulation (EU) 2016/679). This page explains
        your data subject rights, our legal bases for processing, the sub-processors we rely on,
        and how to contact us or your national supervisory authority.
      </p>

      <h2 style={s.h2top}>Data controller</h2>
      <p style={s.p}>
        <strong>{COMPANY_NAME}</strong><br />
        {COMPANY_ADDR}<br />
        Privacy contact:{' '}
        <a href={`mailto:${PRIVACY_EMAIL}`} style={s.a}>{PRIVACY_EMAIL}</a>
      </p>
      <p style={s.p}>
        We do not currently have a designated Data Protection Officer (DPO) as we do not meet the
        mandatory thresholds under Art. 37 GDPR. Privacy matters are handled directly by our founding team.
        This will be reviewed as the company grows.
      </p>

      <h2 style={s.h2}>Legal bases for processing</h2>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Processing activity</th>
            <th style={s.th}>Legal basis</th>
            <th style={s.th}>GDPR article</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['Providing the matching service',           'Performance of a contract',  'Art. 6(1)(b)'],
            ['Sending email digests and match alerts',   'Performance of a contract',  'Art. 6(1)(b)'],
            ['Payment and subscription management',      'Performance of a contract',  'Art. 6(1)(b)'],
            ['Fraud prevention and security',            'Legitimate interests',        'Art. 6(1)(f)'],
            ['Aggregated product analytics',             'Legitimate interests',        'Art. 6(1)(f)'],
            ['Retaining financial records',              'Legal obligation',            'Art. 6(1)(c)'],
            ['Marketing communications (optional)',      'Consent',                     'Art. 6(1)(a)'],
          ].map(([activity, basis, article]) => (
            <tr key={activity}>
              <td style={s.td}>{activity}</td>
              <td style={s.td}>{basis}</td>
              <td style={{ ...s.td, fontFamily: '"JetBrains Mono", monospace', fontSize: 12 }}>{article}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2 style={s.h2}>Your rights under GDPR</h2>
      <p style={s.p}>
        As a data subject, you have the following rights. To exercise any of them, contact us at{' '}
        <a href={`mailto:${PRIVACY_EMAIL}`} style={s.a}>{PRIVACY_EMAIL}</a>. We will respond within
        30 days (extendable to 90 days for complex requests, with notice).
      </p>

      {RIGHTS.map(({ title, desc }) => (
        <div key={title} style={{ marginBottom: 24 }}>
          <h3 style={{ ...s.h3, marginTop: 0 }}>{title}</h3>
          <p style={{ ...s.p, margin: 0 }}>{desc}</p>
        </div>
      ))}

      <div style={s.note}>
        <strong>Identity verification:</strong> To protect your data, we may ask you to verify your identity
        before processing a data subject request. We will not charge a fee for reasonable requests.
      </div>

      <h2 style={s.h2}>Sub-processors and international transfers</h2>
      <p style={s.p}>
        Jyto relies on the following sub-processors. All are bound by data processing agreements.
        Where processors are located outside the EEA, transfers are protected by the mechanisms shown.
      </p>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Processor</th>
            <th style={s.th}>Role</th>
            <th style={s.th}>Location</th>
            <th style={s.th}>Transfer safeguard</th>
          </tr>
        </thead>
        <tbody>
          {SUB_PROCESSORS.map(([name, role, loc, safeguard]) => (
            <tr key={name}>
              <td style={s.td}><strong>{name}</strong></td>
              <td style={s.td}>{role}</td>
              <td style={s.td}>{loc}</td>
              <td style={s.td}>{safeguard}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p style={s.p}>
        SCCs = Standard Contractual Clauses (Commission Decision 2021/914).{' '}
        DPF = EU-US Data Privacy Framework.
      </p>
      <p style={s.p}>
        You may request a copy of the applicable SCCs for any specific processor by emailing{' '}
        <a href={`mailto:${PRIVACY_EMAIL}`} style={s.a}>{PRIVACY_EMAIL}</a>.
      </p>

      <h2 style={s.h2}>Data retention</h2>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Data type</th>
            <th style={s.th}>Retention period</th>
            <th style={s.th}>Basis</th>
          </tr>
        </thead>
        <tbody>
          {RETENTION.map(([type, period, basis]) => (
            <tr key={type}>
              <td style={s.td}>{type}</td>
              <td style={s.td}>{period}</td>
              <td style={s.td}>{basis}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2 style={s.h2}>Security measures</h2>
      <ul style={s.ul}>
        <li style={s.li}>All data in transit is encrypted using HTTPS/TLS</li>
        <li style={s.li}>Passwords are stored as bcrypt hashes — plain-text passwords are never stored or transmitted</li>
        <li style={s.li}>Authentication uses JWT tokens with a 7-day expiry</li>
        <li style={s.li}>Access to production systems is restricted to authorised personnel</li>
        <li style={s.li}>Primary database hosted on Supabase with EU-region storage options</li>
      </ul>
      <p style={s.p}>
        In the event of a personal data breach that is likely to result in a risk to your rights and
        freedoms, we will notify the relevant supervisory authority within 72 hours and affected
        individuals without undue delay, as required by GDPR Art. 33–34.
      </p>

      <h2 style={s.h2}>Automated decision-making</h2>
      <p style={s.p}>
        Jyto uses automated processing to generate match verdicts. However, these verdicts
        do not produce legal effects or similarly significant effects on you — they are informational
        suggestions only. Article 22 GDPR (prohibition on solely automated decisions with legal effects)
        does not apply to this processing.
      </p>
      <p style={s.p}>
        If you believe a verdict has been generated incorrectly or wish a human to review a specific
        match result, please contact us at <a href={`mailto:${PRIVACY_EMAIL}`} style={s.a}>{PRIVACY_EMAIL}</a>.
      </p>

      <h2 style={s.h2}>Right to lodge a complaint</h2>
      <p style={s.p}>
        If you believe we have not handled your personal data in accordance with the GDPR, you have
        the right to lodge a complaint with the data protection supervisory authority in your EU member
        state. A list of EU supervisory authorities is available at{' '}
        <strong>edpb.europa.eu/about-edpb/board/members_en</strong>.
      </p>
      <p style={s.p}>
        We would, however, appreciate the opportunity to address your concern directly before you contact
        a supervisory authority. Please reach out to us first at{' '}
        <a href={`mailto:${PRIVACY_EMAIL}`} style={s.a}>{PRIVACY_EMAIL}</a>.
      </p>

      <h2 style={s.h2}>Contact</h2>
      <p style={s.p}>
        For all data protection and GDPR-related queries:{' '}
        <a href={`mailto:${PRIVACY_EMAIL}`} style={s.a}>{PRIVACY_EMAIL}</a>
      </p>
      <p style={s.p}>
        For a full description of the personal data we collect and how we use it, see our{' '}
        <Link to="/privacy" style={s.a}>Privacy policy</Link>.
      </p>

    </LegalPage>
  );
}
